import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { KoremarginLogo } from '../ui/KoremarginLogo'

interface FormData {
  email: string
}

export function ResetPasswordPage() {
  const { resetPassword } = useAuth()
  const [serverError, setServerError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>()

  async function onSubmit(data: FormData) {
    setServerError(null)
    const err = await resetPassword(data.email)
    if (err) {
      setServerError(err)
    } else {
      setSent(true)
    }
  }

  if (sent) {
    return (
      <div className="min-h-screen bg-[--km-bg] flex items-center justify-center p-4">
        <div className="w-full max-w-sm text-center">
          <div className="flex flex-col items-center mb-8 gap-3">
            <KoremarginLogo variant="full" />
          </div>
          <div className="bg-[--km-surface] border border-[--km-border] rounded-xl p-6 shadow-sm">
            <div className="text-3xl mb-3">✉️</div>
            <h2 className="text-lg font-semibold text-[--km-text-primary] mb-2">Email inviata</h2>
            <p className="text-sm text-[--km-text-secondary]">
              Se l'indirizzo è registrato, riceverai un link per reimpostare la password. Controlla anche la cartella spam.
            </p>
          </div>
          <p className="mt-4 text-sm text-[--km-text-secondary]">
            <Link to="/login" className="text-[--km-primary] hover:underline font-medium">
              Torna al login
            </Link>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[--km-bg] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8 gap-3">
          <h1 className="sr-only">Koremargin</h1>
          <KoremarginLogo variant="full" />
          <p className="text-sm text-[--km-text-secondary]">
            Business Intelligence Suite
          </p>
        </div>

        <div className="bg-[--km-surface] border border-[--km-border] rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-[--km-text-primary] mb-1">Password dimenticata</h2>
          <p className="text-sm text-[--km-text-secondary] mb-5">
            Inserisci la tua email e ti invieremo un link per reimpostare la password.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-[--km-text-secondary] mb-1"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="tu@azienda.it"
                className="w-full px-3 py-2 text-sm border border-[--km-border] rounded-lg bg-[--km-bg] text-[--km-text-primary] placeholder-[--km-text-secondary] focus:outline-none focus:ring-2 focus:ring-[--km-primary] focus:border-transparent"
                {...register('email', {
                  required: 'Email obbligatoria.',
                  pattern: {
                    value: /^\S+@\S+\.\S+$/,
                    message: 'Inserire un indirizzo email valido.',
                  },
                })}
              />
              {errors.email && (
                <p role="alert" className="mt-1 text-xs text-[--km-error]">
                  {errors.email.message}
                </p>
              )}
            </div>

            {serverError && (
              <div role="alert" className="rounded-lg bg-red-50 border border-[--km-error] px-3 py-2">
                <p className="text-sm text-[--km-error]">{serverError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-2 px-4 text-sm font-medium text-white bg-[--km-primary] rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {isSubmitting ? 'Invio in corso…' : 'Invia link di recupero'}
            </button>
          </form>
        </div>

        <p className="mt-4 text-center text-sm text-[--km-text-secondary]">
          <Link to="/login" className="text-[--km-primary] hover:underline font-medium">
            Torna al login
          </Link>
        </p>
      </div>
    </div>
  )
}
